import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import ResourceFileCard from './ResourceFileCard';
import { 
  FileCheck, 
  Plus, 
  FolderOpen, 
  Folder,
  Upload,
  X,
  ChevronRight,
  ChevronDown,
  Eye,
  Trash2
} from 'lucide-react';
import { formatFileSize } from '@/utils/fileUtils';
import { DEFAULT_SHARIAH_PRODUCTS } from '@/config/resourceConfig';

/**
 * ShariaTemplatesManager with folder support
 * Organizes templates by Shariyah product type
 */
const ShariaTemplatesManager = ({
  files,
  onUpload,
  onDelete,
  uploadProgress
}) => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [newFiles, setNewFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [shariahProducts, setShariahProducts] = useState([...DEFAULT_SHARIAH_PRODUCTS]);

  // Organize files by folder/product type
  const organizeFilesByFolder = () => {
    const organized = {};
    
    // Initialize all product folders
    shariahProducts.forEach(product => {
      organized[product] = [];
    });
    
    // Group files by folder
    files.forEach(file => {
      // Extract folder name from S3 key: resources/sharia-templates/{folder}/{filename}
      const parts = file.key.split('/');
      if (parts.length >= 4 && parts[0] === 'resources' && parts[1] === 'sharia-templates') {
        // File is in a folder: resources/sharia-templates/{folder}/{filename}
        const folderName = parts[2];
        if (organized[folderName]) {
          organized[folderName].push(file);
        } else {
          // If folder doesn't exist in current list, add it dynamically
          if (!organized[folderName]) {
            organized[folderName] = [];
          }
          organized[folderName].push(file);
          // Also add to state if not already there
          if (!shariahProducts.includes(folderName)) {
            setShariahProducts(prev => [...prev, folderName]);
          }
        }
      } else if (parts.length === 3 && parts[0] === 'resources' && parts[1] === 'sharia-templates') {
        // Legacy file without folder: resources/sharia-templates/{filename}
        if (!organized['Other']) {
          organized['Other'] = [];
        }
        organized['Other'].push(file);
      } else {
        // Files without proper structure go to "Other"
        if (!organized['Other']) {
          organized['Other'] = [];
        }
        organized['Other'].push(file);
      }
    });
    
    return organized;
  };

  const filesByFolder = organizeFilesByFolder();

  const handleFolderClick = (folderName) => {
    setSelectedFolder(folderName);
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderName)) {
        newSet.delete(folderName);
      } else {
        newSet.add(folderName);
      }
      return newSet;
    });
  };

  const handleUploadClick = (folderName) => {
    setSelectedFolder(folderName);
    setShowUploadModal(true);
  };

  const checkDuplicateFile = (fileName, folderName) => {
    // Get current files organized by folder (recompute to ensure we have latest)
    const currentFilesByFolder = organizeFilesByFolder();
    const folderFiles = currentFilesByFolder[folderName] || [];
    
    // Normalize the new file name for comparison
    const normalizedNewFileName = fileName.toLowerCase().trim();
    
    return folderFiles.some(existingFile => {
      // Extract original filename from S3 key (remove timestamp prefix if present)
      // S3 key format: resources/sharia-templates/{folder}/{timestamp}_{filename}
      const keyParts = existingFile.key.split('/');
      const s3FileName = keyParts[keyParts.length - 1];
      
      // Remove timestamp prefix (format: timestamp_filename)
      // Timestamp is typically numeric, so we look for the first underscore followed by the actual filename
      let originalFileName = s3FileName;
      if (s3FileName.includes('_')) {
        // Check if it starts with a timestamp (numeric prefix)
        const parts = s3FileName.split('_');
        if (parts.length > 1 && /^\d+$/.test(parts[0])) {
          // First part is a timestamp, join the rest
          originalFileName = parts.slice(1).join('_');
        }
      }
      
      return originalFileName.toLowerCase().trim() === normalizedNewFileName;
    });
  };

  const handleUpload = async () => {
    if (!newFiles || newFiles.length === 0 || !selectedFolder) return;
    
    // Filter out duplicate files
    const filesToUpload = [];
    const duplicateFiles = [];
    
    for (const file of newFiles) {
      if (checkDuplicateFile(file.name, selectedFolder)) {
        duplicateFiles.push(file.name);
      } else {
        filesToUpload.push(file);
      }
    }
    
    // Show warning if duplicates found
    if (duplicateFiles.length > 0) {
      const duplicateMsg = duplicateFiles.length === 1
        ? `File "${duplicateFiles[0]}" already exists and will be skipped.`
        : `${duplicateFiles.length} files already exist and will be skipped:\n${duplicateFiles.map(f => `- ${f}`).join('\n')}`;
      
      if (filesToUpload.length === 0) {
        alert(duplicateMsg + '\n\nNo new files to upload.');
        return;
      } else {
        const proceed = window.confirm(duplicateMsg + `\n\nProceed with uploading ${filesToUpload.length} new file(s)?`);
        if (!proceed) return;
      }
    }
    
    if (filesToUpload.length === 0) return;
    
    setIsUploading(true);
    try {
      // Upload all non-duplicate files sequentially
      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i];
        await onUpload(file, 'sharia-templates', selectedFolder, i, filesToUpload.length);
      }
      setNewFiles([]);
      setShowUploadModal(false);
      setSelectedFolder(null);
    } catch (error) {
      console.error('Template upload error:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleModalClose = () => {
    if (!isUploading) {
      setShowUploadModal(false);
      setNewFiles([]);
      setSelectedFolder(null);
    }
  };

  const handleCreateFolder = () => {
    if (newFolderName.trim()) {
      const folderName = newFolderName.trim();
      if (!shariahProducts.includes(folderName)) {
        setShariahProducts(prev => [...prev, folderName]);
      }
      setNewFolderName('');
      setShowNewFolderInput(false);
      setSelectedFolder(folderName);
      setExpandedFolders(prev => new Set(prev).add(folderName));
    }
  };

  const handleFileView = (file) => {
    window.open(file.url, '_blank');
  };

  const handleFileDownload = (file) => {
    window.open(file.url, '_blank');
  };

  const handleFileDelete = (file) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      onDelete(file.key, 'sharia-templates');
    }
  };

  const totalFiles = files.length;

  return (
    <div className="space-y-6">
      {/* Header with Create Folder */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Shariyah Product Templates</h3>
          <p className="text-sm text-gray-600 mt-1">
            Organize templates by Shariyah product type
          </p>
        </div>
        <div className="flex gap-2">
          {!showNewFolderInput ? (
            <Button
              variant="outline"
              onClick={() => setShowNewFolderInput(true)}
              className="flex items-center gap-2"
            >
              <Folder className="w-4 h-4" />
              New Folder
            </Button>
          ) : (
            <div className="flex gap-2">
              <Input
                placeholder="Folder name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateFolder();
                  }
                }}
                className="w-48"
                autoFocus
              />
              <Button
                onClick={handleCreateFolder}
                disabled={!newFolderName.trim()}
                size="sm"
              >
                Create
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowNewFolderInput(false);
                  setNewFolderName('');
                }}
                size="sm"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Upload Progress */}
      {uploadProgress && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md">
          <p className="text-emerald-800 text-sm">{uploadProgress}</p>
        </div>
      )}

      {/* Folders Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {shariahProducts.map((folderName) => {
          const folderFiles = filesByFolder[folderName] || [];
          const fileCount = folderFiles.length;
          const isExpanded = expandedFolders.has(folderName);
          const isSelected = selectedFolder === folderName;

          return (
            <Card 
              key={folderName}
              className={`hover:shadow-md transition-all ${
                isSelected ? 'ring-2 ring-emerald-500' : ''
              }`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div 
                    className="flex items-center gap-3 flex-1 cursor-pointer"
                    onClick={() => handleFolderClick(folderName)}
                  >
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <FolderOpen className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-base font-semibold text-gray-900 mb-1">
                        {folderName}
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {fileCount} {fileCount === 1 ? 'template' : 'templates'}
                        </Badge>
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    )}
                  </div>
                </div>
              </CardHeader>
              
              {isExpanded && (
                <CardContent className="space-y-4">
                  {/* Upload Button */}
                  <Button
                    onClick={() => handleUploadClick(folderName)}
                    className="w-full flex items-center gap-2"
                    size="sm"
                  >
                    <Upload className="w-4 h-4" />
                    Upload Template
                  </Button>

                  {/* Files List */}
                  {fileCount > 0 ? (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {folderFiles.map((file) => (
                        <div
                          key={file.key}
                          className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <FileCheck className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {file.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(file.size)}
                            </p>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleFileView(file)}
                              className="h-7 w-7 p-0"
                            >
                              <Eye className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleFileDelete(file)}
                              className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-500">
                      <FileCheck className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      <p className="text-sm">No templates in this folder</p>
                      <p className="text-xs mt-1">Click "Upload Template" to add files</p>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Empty State - if no folders have files */}
      {totalFiles === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-gray-500">
          <div className="p-4 bg-emerald-100 rounded-full mb-4">
            <FileCheck className="w-8 h-8 text-emerald-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Sharia Templates</h3>
          <p className="text-sm text-gray-600 text-center max-w-md">
            Click on a folder above to expand it and upload Sharia compliance templates.
          </p>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && selectedFolder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Upload Template to {selectedFolder}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Files (Multiple files allowed)
                </label>
                <Input
                  type="file"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setNewFiles(files);
                  }}
                  accept=".pdf,.doc,.docx,.rtf,.xls,.xlsx,.odt,.ods,.txt,.csv,.jpg,.jpeg,.png"
                />
                {newFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <p className="text-sm font-medium text-gray-700">
                      Selected {newFiles.length} file{newFiles.length !== 1 ? 's' : ''}:
                    </p>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {newFiles.map((file, index) => {
                        const isDuplicate = selectedFolder && checkDuplicateFile(file.name, selectedFolder);
                        return (
                          <div 
                            key={index} 
                            className={`text-sm p-2 rounded ${
                              isDuplicate 
                                ? 'bg-yellow-50 border border-yellow-200' 
                                : 'bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className={`font-medium ${isDuplicate ? 'text-yellow-800' : 'text-gray-600'}`}>
                                {file.name}
                              </span>
                              {isDuplicate && (
                                <span className="text-xs bg-yellow-200 text-yellow-800 px-1.5 py-0.5 rounded">
                                  Already exists
                                </span>
                              )}
                              <span className={`ml-auto ${isDuplicate ? 'text-yellow-600' : 'text-gray-500'}`}>
                                ({formatFileSize(file.size)})
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {selectedFolder && newFiles.some(f => checkDuplicateFile(f.name, selectedFolder)) && (
                      <p className="text-xs text-yellow-700 mt-2">
                        ⚠️ Duplicate files will be skipped during upload
                      </p>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
            <div className="flex justify-end gap-3 p-6 pt-0">
              <Button
                variant="outline"
                onClick={handleModalClose}
                disabled={isUploading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={!newFiles || newFiles.length === 0 || isUploading}
                className="flex items-center gap-2"
              >
                {isUploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload
                  </>
                )}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ShariaTemplatesManager;

